import React, { FC, useEffect, useRef } from 'react';
import { View } from 'react-native';
import Animated, { SequencedTransition } from 'react-native-reanimated';
import { useIsFocused } from '@react-navigation/native';
import { scale } from '@components/frame';
import { useManageTimeTiles } from './hooks/useManageTimeTiles';
import TimeTile from './components/TimeTile';
import { EventTimelineData, OptimisticUpdateCallbacks } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { TILE_HEIGHT, SWAP_ANIMATION_DURATION } from './constants';
import { NCFileBatchEventsProps } from '@v2/pages/common/components/NCFileBatchEvents';
import { EventBarChartProps } from '@v2/pages/common/components/NCFileBatchEvents/components/EventBarChart';
import { useCurveLinesContextOptional } from '@v2/pages/common/components/NCFileBatchEvents/context/CurveLinesContext';

export interface TimeTilesProps
  extends Pick<NCFileBatchEventsProps, 'eventType' | 'ncFileId'>,
    Pick<EventBarChartProps, 'eventsPerspective'>,
    OptimisticUpdateCallbacks {
  eventTimelineData: EventTimelineData | undefined;
  isDataLoaded?: boolean;
}

const TimeTiles: FC<TimeTilesProps> = ({
  eventTimelineData,
  eventType,
  eventsPerspective,
  ncFileId,
  onOptimisticUpdate,
  onOptimisticUpdateError,
  isDataLoaded,
}) => {
  const { timeTilesConfig, isReadyToShow, ncFileIdForUpdate, shouldAnimate, remeasureTrigger } = useManageTimeTiles({
    eventTimelineData,
    eventsPerspective,
    ncFileId,
    isDataLoaded,
  });

  const curveLinesContext = useCurveLinesContextOptional();
  const prevIsReadyToShow = useRef(isReadyToShow);
  const isFocused = useIsFocused();
  const wasFocused = useRef(isFocused);

  // Reset curve line positions when transitioning from ready to not-ready (e.g., when switching machines)
  // Use resetLinePositions to preserve containerOffset which is only measured on mount
  useEffect(() => {
    if (prevIsReadyToShow.current && !isReadyToShow && curveLinesContext) {
      curveLinesContext.resetLinePositions();
    }
    prevIsReadyToShow.current = isReadyToShow;
  }, [isReadyToShow, curveLinesContext]);

  // Reset curve line positions when tab loses or gains focus
  // - On lose focus: clear positions so the next tab doesn't see stale data
  // - On gain focus: clear positions in case another tab registered stale data
  useEffect(() => {
    if (wasFocused.current && !isFocused && curveLinesContext) {
      curveLinesContext.resetLinePositions();
    }
    if (!wasFocused.current && isFocused && curveLinesContext) {
      curveLinesContext.resetLinePositions();
    }
    wasFocused.current = isFocused;
  }, [isFocused, curveLinesContext]);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(18), minHeight: scale(TILE_HEIGHT) }}>
      {timeTilesConfig.map((config) => (
        <Animated.View
          key={config.timeCategory}
          style={{ flex: 1, height: scale(TILE_HEIGHT) }}
          layout={shouldAnimate ? SequencedTransition.duration(SWAP_ANIMATION_DURATION) : undefined}
        >
          <TimeTile
            timeTileConfig={config}
            isReadyToShow={isReadyToShow}
            eventType={eventType}
            eventTimelineData={eventTimelineData}
            ncFileIdForUpdate={ncFileIdForUpdate}
            onOptimisticUpdate={onOptimisticUpdate}
            onOptimisticUpdateError={onOptimisticUpdateError}
            remeasureTrigger={remeasureTrigger}
          />
        </Animated.View>
      ))}
    </View>
  );
};

export default TimeTiles;
